import type { CRMUser, EnhancedDeal, TimberProject } from '../types';
import {
  architectDisplayName,
  getAssignedArchitectUids,
  listArchitects,
} from './architectProjectAccess';
import { isArchitectRole } from './userRoles';

export type CommentAudience = 'internal' | 'architect';

export function isDesignGroupCommentKey(key: string | null | undefined): boolean {
  return typeof key === 'string' && key.endsWith('::design');
}

/** Missing / unknown audience is working notes (safe default). */
export function normalizeCommentAudience(value: unknown): CommentAudience {
  return value === 'architect' ? 'architect' : 'internal';
}

export function isArchitectDecisionAudience(value: unknown): boolean {
  return normalizeCommentAudience(value) === 'architect';
}

/** Query/notification value that picks a Design group comments tab. */
export type GroupCommentsDiscussion = 'architect' | 'comments' | 'pdf';

export function parseGroupCommentsDiscussion(value: unknown): GroupCommentsDiscussion | undefined {
  if (value === 'architect' || value === 'comments' || value === 'pdf') return value;
  if (value === 'internal') return 'comments';
  return undefined;
}

/**
 * Email, bell, and Slack links should land on the matching Design tab.
 * Explicit `discussion=` wins; otherwise architect-audience comments open Architect Discussion.
 */
export function resolveGroupCommentsTab(params: {
  discussion?: unknown;
  commentAudience?: unknown;
  groupCommentKey?: string | null;
}): 'architect' | 'comments' | 'pdf' {
  const parsed = parseGroupCommentsDiscussion(params.discussion);
  if (parsed === 'pdf') return 'pdf';
  const design = !params.groupCommentKey || isDesignGroupCommentKey(params.groupCommentKey);
  if (parsed === 'architect' && design) return 'architect';
  if (parsed === 'comments') return 'comments';
  if (design && isArchitectDecisionAudience(params.commentAudience)) return 'architect';
  return 'comments';
}

/** Persist on Design-group notifications so the link can open the right tab without a comment fetch. */
export function discussionForGroupComment(
  groupCommentKey: string | null | undefined,
  audience: unknown,
): GroupCommentsDiscussion | undefined {
  if (!isDesignGroupCommentKey(groupCommentKey)) return undefined;
  return isArchitectDecisionAudience(audience) ? 'architect' : 'comments';
}

export function filterArchitectDecisionComments<T extends { audience?: string | null }>(
  comments: T[],
): T[] {
  return comments.filter((c) => isArchitectDecisionAudience(c.audience));
}

export function filterInternalComments<T extends { audience?: string | null }>(
  comments: T[],
): T[] {
  return comments.filter((c) => !isArchitectDecisionAudience(c.audience));
}

/** Separate read-receipt key so opening Design Comments does not clear the Architect tab badge. */
export function architectDiscussionReceiptKey(groupKey: string): string {
  return `${groupKey}::architect`;
}

export function architectDiscussionUnreadCount(
  comments: Array<{ audience?: string | null; createdAt?: Date | string | number; authorId?: string }>,
  receipts: Record<string, number>,
  groupKey: string,
  userId?: string,
): number {
  if (!groupKey) return 0;
  const readAt = receipts[architectDiscussionReceiptKey(groupKey)] || 0;
  return comments.filter((c) => {
    if (!isArchitectDecisionAudience(c.audience)) return false;
    if (userId && c.authorId === userId) return false;
    const raw = c.createdAt;
    const t = raw instanceof Date
      ? raw.getTime()
      : raw != null
        ? new Date(raw).getTime()
        : NaN;
    return !Number.isNaN(t) && t > readAt;
  }).length;
}

export function assignedArchitectLabel(
  crmUsers: CRMUser[],
  deal?: EnhancedDeal | null,
  project?: TimberProject | null,
): string {
  const uids = getAssignedArchitectUids(deal, project);
  const names = uids.map((uid) => architectDisplayName(uid, crmUsers)).filter(Boolean);
  if (names.length === 1) return names[0];
  if (names.length > 1) return names.slice(0, 2).join(' & ');
  const firm = (deal?.assignedArchitectName || '').trim();
  if (firm) return firm;
  return 'the architect';
}

export function excludeArchitectsFromMentions<T extends { role?: string }>(users: T[]): T[] {
  return users.filter((u) => !isArchitectRole(u.role));
}

export function mentionUsersForCommentAudience<T extends { role?: string }>(
  users: T[],
  audience: CommentAudience,
  viewerIsArchitect: boolean,
): T[] {
  if (viewerIsArchitect || audience === 'architect') return users;
  return excludeArchitectsFromMentions(users);
}

export function notifyUserIdsForCommentAudience(
  baseIds: string[] | null | undefined,
  audience: CommentAudience,
  crmUsers: CRMUser[],
  deal?: EnhancedDeal | null,
  project?: TimberProject | null,
): string[] | null {
  const assigned = getAssignedArchitectUids(deal, project);
  const architectIds = new Set([
    ...assigned,
    ...listArchitects(crmUsers).map((u) => u.uid),
  ]);
  const base = (baseIds || []).filter(Boolean);
  if (audience === 'architect') {
    const merged = [...base.filter((id) => !architectIds.has(id)), ...assigned];
    return merged.length ? Array.from(new Set(merged)) : assigned.length ? assigned : null;
  }
  const stripped = base.filter((id) => !architectIds.has(id));
  return stripped.length ? stripped : null;
}
